/**
 * Explainability event log — a self-contained UI module + log store.
 *
 * Builds a scrollable timeline panel that ties simulation events (member
 * failures, and — in future — resonance / galloping / ice events) to the
 * root-cause PHENOMENON that explains them. Each row shows the time, a severity
 * dot, the event title, and the phenomenon name; expanding a row reveals the
 * phenomenon's explanation and a "Learn more" link to a real citation.
 *
 * Like failureReplay.ts this module owns ONLY its own DOM (built inside the
 * `container` passed to the constructor) and is driven entirely by the
 * maintainer calling `log()` from the frame loop. It has no knowledge of the
 * solver — the caller maps solver events to LogEntry (see docs/PHENOMENA.md for
 * the exact hook points and failure → phenomenon mapping).
 *
 * The caller passes `time`; this module never reads the clock (Math.random /
 * argless Date are unavailable and unnecessary here).
 */

import { getPhenomenon, type Phenomenon } from "@/physics/phenomena";

export interface LogEntry {
  /** Simulation time in seconds (caller-supplied). */
  time: number;
  /** Key into the phenomena catalog. Unknown ids degrade gracefully. */
  phenomenonId: string;
  /** Human title, e.g. "L3×3 bracing buckled at 24 m". */
  title: string;
  /** Optional extra line shown when expanded (e.g. "util 118% · leg C"). */
  detail?: string;
  /** Optional member label for reference. */
  memberLabel?: string;
  /** Optional world position of the event. */
  position?: { x: number; y: number; z: number };
  severity?: "info" | "warn" | "critical";
}

const SEVERITY_COLOR: Record<NonNullable<LogEntry["severity"]>, string> = {
  info: "#5aa9e6",
  warn: "#e6b450",
  critical: "#e5484d",
};

const CATEGORY_LABEL: Record<Phenomenon["category"], string> = {
  structural: "Structural",
  aeroelastic: "Aeroelastic",
  geotechnical: "Geotechnical",
  loading: "Loading",
  material: "Material",
};

export class EventLog {
  private readonly root: HTMLDivElement;
  private readonly list: HTMLDivElement;
  private readonly emptyHint: HTMLDivElement;
  private count = 0;

  constructor(container: HTMLElement) {
    const root = document.createElement("div");
    root.style.cssText = [
      "display:flex",
      "flex-direction:column",
      "gap:8px",
      "padding:10px",
      "border-radius:8px",
      "background:#0b0e13",
      "border:1px solid #232c38",
      "font:13px/1.4 system-ui,sans-serif",
      "color:#dfe6ef",
      "max-height:340px",
    ].join(";");

    const header = document.createElement("div");
    header.textContent = "Event log";
    header.style.cssText = [
      "font-weight:700",
      "letter-spacing:0.02em",
      "color:#aebccd",
      "text-transform:uppercase",
      "font-size:11px",
    ].join(";");

    const list = document.createElement("div");
    list.style.cssText = [
      "display:flex",
      "flex-direction:column",
      "gap:6px",
      "overflow-y:auto",
      "flex:1",
      "min-height:0",
      "padding-right:2px",
    ].join(";");

    const emptyHint = document.createElement("div");
    emptyHint.textContent = "No events yet — raise the wind and watch what breaks.";
    emptyHint.style.cssText = "color:#6b7a8d;font-style:italic;padding:4px 2px";
    list.appendChild(emptyHint);

    root.append(header, list);
    container.appendChild(root);

    this.root = root;
    this.list = list;
    this.emptyHint = emptyHint;
  }

  /**
   * Append one event to the timeline (newest at top). Safe to call every frame;
   * the caller is responsible for de-duplicating repeated events if desired.
   */
  log(entry: LogEntry): void {
    if (this.count === 0) this.emptyHint.style.display = "none";
    this.count++;
    const row = this.buildRow(entry);
    // Newest at top.
    this.list.insertBefore(row, this.emptyHint.nextSibling);
  }

  /** Clear all entries. Called on tower rebuild / reset. */
  reset(): void {
    while (this.emptyHint.nextSibling) this.list.removeChild(this.emptyHint.nextSibling);
    this.count = 0;
    this.emptyHint.style.display = "";
  }

  dispose(): void {
    this.root.remove();
  }

  // --- DOM construction ---

  private buildRow(entry: LogEntry): HTMLDetailsElement {
    const phenomenon = getPhenomenon(entry.phenomenonId);
    const severity = entry.severity ?? "info";
    const color = SEVERITY_COLOR[severity];

    const row = document.createElement("details");
    row.style.cssText = [
      "border:1px solid #1c2530",
      "border-radius:6px",
      "background:#0e131b",
      "overflow:hidden",
    ].join(";");

    // --- summary line (always visible) ---
    const summary = document.createElement("summary");
    summary.style.cssText = [
      "display:flex",
      "align-items:center",
      "gap:8px",
      "padding:7px 9px",
      "cursor:pointer",
      "list-style:none",
      "user-select:none",
    ].join(";");

    const dot = document.createElement("span");
    dot.style.cssText = [
      "flex:0 0 auto",
      "width:9px",
      "height:9px",
      "border-radius:50%",
      `background:${color}`,
      `box-shadow:0 0 6px ${color}`,
    ].join(";");
    dot.setAttribute("aria-label", `${severity} severity`);

    const t = document.createElement("span");
    t.textContent = `t=${entry.time.toFixed(1)}s`;
    t.style.cssText = [
      "flex:0 0 auto",
      "color:#8fa2b6",
      "font-variant-numeric:tabular-nums",
      "font-size:12px",
      "min-width:48px",
    ].join(";");

    const title = document.createElement("span");
    title.textContent = entry.title;
    title.style.cssText = "flex:1 1 auto;font-weight:600;color:#eef3f9";

    const tag = document.createElement("span");
    tag.textContent = phenomenon ? phenomenon.name : entry.phenomenonId;
    tag.style.cssText = [
      "flex:0 0 auto",
      "font-size:11px",
      "color:#9fb0c4",
      "background:#161d27",
      "border:1px solid #232c38",
      "border-radius:10px",
      "padding:1px 8px",
      "white-space:nowrap",
    ].join(";");

    summary.append(dot, t, title, tag);

    // --- expandable body ---
    const body = document.createElement("div");
    body.style.cssText = [
      "padding:2px 9px 10px 27px",
      "display:flex",
      "flex-direction:column",
      "gap:6px",
      "border-top:1px solid #161d27",
    ].join(";");

    if (entry.detail) {
      const detail = document.createElement("div");
      detail.textContent = entry.detail;
      detail.style.cssText = "color:#c7d2df;font-size:12px";
      body.appendChild(detail);
    }

    if (entry.memberLabel) {
      const label = document.createElement("div");
      label.textContent = `Member: ${entry.memberLabel}`;
      label.style.cssText = "color:#8fa2b6;font-size:11px;font-variant-numeric:tabular-nums";
      body.appendChild(label);
    }

    if (phenomenon) {
      const cat = document.createElement("div");
      cat.textContent = CATEGORY_LABEL[phenomenon.category];
      cat.style.cssText = [
        "align-self:flex-start",
        "font-size:10px",
        "text-transform:uppercase",
        "letter-spacing:0.04em",
        "color:#7d8ea1",
      ].join(";");

      const explanation = document.createElement("div");
      explanation.textContent = phenomenon.explanation;
      explanation.style.cssText = "color:#c7d2df;font-size:12px;line-height:1.5";

      const link = document.createElement("a");
      link.href = phenomenon.moreInfoUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = "Learn more ↗";
      link.style.cssText = [
        "align-self:flex-start",
        "color:#6cb6ff",
        "text-decoration:none",
        "font-weight:600",
        "font-size:12px",
      ].join(";");

      body.append(cat, explanation, link);
    } else {
      // Unknown phenomenonId — still useful, just no citation.
      const unknown = document.createElement("div");
      unknown.textContent = `No catalog entry for "${entry.phenomenonId}".`;
      unknown.style.cssText = "color:#8fa2b6;font-size:12px;font-style:italic";
      body.appendChild(unknown);
    }

    row.append(summary, body);
    return row;
  }
}
